/* ==========================================================================
   ABEERS Wholesale — Interactive Brochure Book
   Architecture:
   - The closed hardcover graphic is pure CSS/HTML, always present, never
     depends on any script/network — so there is never a blank/broken state.
   - PDF.js decodes the uploaded brochure PDF into page images in the
     background (starts as soon as the section loads, not on click), cached
     as data URLs so opening later is instant.
   - StPageFlip (https://github.com/Nodlik/StPageFlip) renders the actual
     open two-page spread and owns all page-turn physics: paper curl,
     corner-follow dragging, spine resistance, shadows, touch swipe. This is
     a purpose-built library for exactly this interaction — reimplementing
     mesh-based paper deformation by hand would not reach the same quality.
   - The cover-open/close rotation and the anchored "grow out of the book"
     expand/collapse of the spread panel are custom, tied to the book's own
     on-screen position so nothing jumps or pops in as a separate modal.
   - On mobile (stacked single-column layout) the anchored grow-left-over-
     hero-copy behaviour has no room to work in, so the panel switches to a
     centered, fixed, near-full-screen presentation instead.
   ========================================================================== */
(function () {
  'use strict';

  var PDFJS_VERSION = '3.11.174';
  var PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/' + PDFJS_VERSION + '/';
  var PAGEFLIP_SRC = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js';
  var RENDER_SCALE = 2.0;   // base — multiplied by device pixel ratio at render time
  var JPEG_QUALITY = 0.96;
  var INITIAL_BATCH = 4;   // pages rendered before we consider the book "ready to open"
  var CONCURRENCY = 2;     // pages rendered in parallel during the background pass
  var MAX_PAGES_TO_PRERENDER = 60; // safety cap for very long PDFs

  var PLACEHOLDER_DATA_URL = (function () {
    var c = document.createElement('canvas');
    c.width = 4; c.height = 6;
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#F8F4EF';
    ctx.fillRect(0, 0, 4, 6);
    return c.toDataURL('image/png');
  })();

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[data-aw-src="' + src + '"]')) { resolve(); return; }
      var s = document.createElement('script');
      s.src = src;
      s.setAttribute('data-aw-src', src);
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function ensurePdfJs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    return loadScript(PDFJS_CDN + 'pdf.min.js').then(function () {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_CDN + 'pdf.worker.min.js';
      return window.pdfjsLib;
    });
  }

  function getPageFlipCtor() {
    if (window.St && window.St.PageFlip) return window.St.PageFlip;
    if (window.PageFlip) return window.PageFlip;
    return null;
  }

  function ensurePageFlip() {
    var existing = getPageFlipCtor();
    if (existing) return Promise.resolve(existing);
    return loadScript(PAGEFLIP_SRC).then(function () {
      var ctor = getPageFlipCtor();
      if (!ctor) throw new Error('PageFlip failed to expose a global constructor');
      return ctor;
    });
  }

  function runIdle(fn) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: 2500 });
    } else {
      setTimeout(fn, 400);
    }
  }

  function AbeersBook(root) {
    this.root = root;
    this.book = root.querySelector('.aw-book');
    this.heroGrid = root.querySelector('.aw-hero-grid');
    this.shell = root.querySelector('[data-aw-shell]');
    this.mount = root.querySelector('[data-aw-mount]');
    this.loadingEl = root.querySelector('[data-aw-loading]');
    this.closeBtn = root.querySelector('[data-aw-close]');
    this.prevBtn = root.querySelector('[data-aw-prev]');
    this.nextBtn = root.querySelector('[data-aw-next]');
    this.pageCountEl = root.querySelector('[data-aw-page-count]');

    this.pdfUrl = root.getAttribute('data-pdf-url') || '';
    this.fallbackImages = JSON.parse(root.getAttribute('data-fallback-images') || '[]');
    this.showCover = this.shell.getAttribute('data-show-cover') === 'true';

    this.pdfDoc = null;
    this.totalPages = 0;
    this.pageUrls = [];      // ordered array of data URLs / image srcs, index 0 = page 1
    this.assetsReady = false;
    this.isOpen = false;
    this.pageFlip = null;
    this.usingLegacyFallback = false;
    this.legacy = null;      // legacy simple-flip instance, only created if PageFlip can't load

    this.preload();
    this.bindEvents();
  }

  // ---- Background preload: starts immediately, never blocks the hero ----
  AbeersBook.prototype.preload = function () {
    var self = this;

    // Fetching the libraries is network work, not CPU work — start it right
    // away rather than waiting for an idle slot, so they're already in
    // memory by the time the (idle-deferred) rendering pass needs them.
    var libsReady = Promise.all([
      ensurePdfJs().catch(function () { return null; }),
      ensurePageFlip().catch(function () { return null; })
    ]);

    libsReady.then(function (results) {
      var pdfjsLib = results[0];
      self.pageFlipAvailable = !!results[1];

      if (!self.pdfUrl || !pdfjsLib) {
        self.pageUrls = self.fallbackImages.slice();
        self.totalPages = self.pageUrls.length;
        self.assetsReady = true;
        return;
      }

      // The actual PDF parse + first render pass is CPU work — defer that
      // slice to an idle moment so it never competes with hero paint.
      runIdle(function () {
        pdfjsLib.getDocument(self.pdfUrl).promise.then(function (doc) {
          self.pdfDoc = doc;
          self.totalPages = Math.min(doc.numPages, MAX_PAGES_TO_PRERENDER);
          self.pageUrls = new Array(self.totalPages).fill(PLACEHOLDER_DATA_URL);
          return self.renderPageRange(1, Math.min(INITIAL_BATCH, self.totalPages));
        }).then(function () {
          // Ready as soon as the first spread's worth of pages exist —
          // opening no longer waits for the whole brochure to render.
          self.assetsReady = true;
          self.continueBackgroundRender(INITIAL_BATCH + 1);
        }).catch(function () {
          self.pdfDoc = null;
          self.pageUrls = self.fallbackImages.slice();
          self.totalPages = self.pageUrls.length;
          self.assetsReady = true;
        });
      });
    });
  };

  // Renders pages [start, end] (inclusive) with limited concurrency so a
  // long brochure doesn't spike the main thread all at once.
  AbeersBook.prototype.renderPageRange = function (start, end) {
    var self = this;
    var next = start;

    function renderOne(pageNum) {
      return self.pdfDoc.getPage(pageNum).then(function (page) {
        var dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        var viewport = page.getViewport({ scale: RENDER_SCALE * dpr });
        var canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        var ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        return page.render({ canvasContext: ctx, viewport: viewport }).promise.then(function () {
          self.pageUrls[pageNum - 1] = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        });
      });
    }

    function worker() {
      if (next > end) return Promise.resolve();
      var pageNum = next++;
      return renderOne(pageNum).then(worker);
    }

    var workers = [];
    for (var i = 0; i < CONCURRENCY; i++) workers.push(worker());
    return Promise.all(workers);
  };

  // After the book is already openable, keep rendering the remaining pages
  // quietly in the background (idle-scheduled, chunked) so flipping deep
  // into a long brochure never hits an unrendered page.
  AbeersBook.prototype.continueBackgroundRender = function (fromPage) {
    var self = this;
    if (fromPage > this.totalPages) return;

    runIdle(function () {
      var chunkEnd = Math.min(fromPage + INITIAL_BATCH - 1, self.totalPages);
      self.renderPageRange(fromPage, chunkEnd).then(function () {
        // If the book is already open, hand the newly-rendered pages to
        // StPageFlip without disturbing whatever page the reader is on.
        if (self.pageFlip && typeof self.pageFlip.updateFromImages === 'function') {
          try { self.pageFlip.updateFromImages(self.pageUrls); } catch (e) { /* no-op */ }
        }
        self.continueBackgroundRender(chunkEnd + 1);
      });
    });
  };

  AbeersBook.prototype.bindEvents = function () {
    var self = this;

    this.book.addEventListener('mouseenter', function () {
      if (!self.isOpen) self.book.classList.add('is-hovering');
    });
    this.book.addEventListener('mouseleave', function () {
      self.book.classList.remove('is-hovering');
    });
    this.book.addEventListener('click', function () { self.open(); });
    this.book.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.open(); }
    });

    this.closeBtn.addEventListener('click', function () { self.close(); });

    document.addEventListener('click', function (e) {
      if (!self.isOpen) return;
      var withinShell = self.shell.contains(e.target);
      var isBookClick = self.book.contains(e.target);
      if (!withinShell && !isBookClick) self.close();
    });

    this.prevBtn.addEventListener('click', function () { self.turnPrev(); });
    this.nextBtn.addEventListener('click', function () { self.turnNext(); });

    document.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;
      if (e.key === 'Escape') self.close();
      if (e.key === 'ArrowRight') self.turnNext();
      if (e.key === 'ArrowLeft') self.turnPrev();
    });

    window.addEventListener('resize', function () {
      if (self.isOpen) self.positionShell(true);
    });
  };

  AbeersBook.prototype.isMobile = function () {
    return window.matchMedia('(max-width: 700px)').matches;
  };

  // Anchors the shell to the book's current on-screen footprint so the
  // opening animation grows out of the book rather than jumping to a
  // separate position. On mobile it's just a centered fixed panel (handled
  // entirely by CSS media query), so no measurement is needed there.
  AbeersBook.prototype.positionShell = function (skipTransition) {
    if (this.isMobile()) return; // CSS handles mobile positioning
    var bookRect = this.book.getBoundingClientRect();
    var gridRect = this.heroGrid.getBoundingClientRect();

    var pageWidth = Math.max(bookRect.width, 300);
    var pageHeight = Math.max(bookRect.height, 420);
    var spreadWidth = pageWidth * 2;

    var right = gridRect.right - bookRect.right;
    var top = bookRect.top - gridRect.top;

    if (skipTransition) this.shell.style.transition = 'none';
    this.shell.style.right = right + 'px';
    this.shell.style.top = top + 'px';
    this.shell.style.width = spreadWidth + 'px';
    this.shell.style.height = pageHeight + 'px';
    if (skipTransition) {
      void this.shell.offsetWidth;
      this.shell.style.transition = '';
    }
    this._pageWidth = pageWidth;
    this._pageHeight = pageHeight;
  };

  AbeersBook.prototype.open = function () {
    var self = this;
    if (this.isOpen) return;

    // Cancel any pending cleanup from a just-closed instance so a quick
    // reopen can't have its fresh mount wiped out from under it.
    if (this._closeCleanupTimer) {
      clearTimeout(this._closeCleanupTimer);
      this._closeCleanupTimer = null;
    }
    this.teardownReader();

    this.isOpen = true;
    this.book.classList.remove('is-hovering');
    this.book.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (!this.isMobile()) {
      // Start the shell exactly at the book's own footprint (scaleX ~0.5,
      // i.e. one page-width) so it visibly grows out of the closed book
      // instead of appearing from nowhere.
      this.positionShell(true);
      this.shell.style.transform = 'scaleX(0.5)';
    }

    requestAnimationFrame(function () {
      self.shell.classList.add('is-active');
      if (!self.isMobile()) self.shell.style.transform = 'scaleX(1)';
    });

    if (this.assetsReady) {
      // Small delay so the expand animation is visibly under way first —
      // feels like the pages are unfurling, not just appearing mid-motion.
      setTimeout(function () { self.mountReader(); }, 260);
    } else {
      this.showLoading(true);
      this.waitForAssets().then(function () { self.mountReader(); });
    }
  };

  AbeersBook.prototype.waitForAssets = function () {
    var self = this;
    return new Promise(function (resolve) {
      (function poll() {
        if (self.assetsReady) { resolve(); return; }
        setTimeout(poll, 150);
      })();
    });
  };

  AbeersBook.prototype.showLoading = function (visible) {
    this.loadingEl.classList.toggle('is-visible', !!visible);
  };

  AbeersBook.prototype.mountReader = function () {
    this.showLoading(false);
    if (!this.pageUrls.length) return; // nothing to show; shell stays open but empty rather than erroring

    if (this.pageFlipAvailable && !this.usingLegacyFallback) {
      this.mountPageFlip();
    } else {
      this.mountLegacyFlip();
    }
  };

  AbeersBook.prototype.mountPageFlip = function () {
    var self = this;
    var PageFlipCtor = getPageFlipCtor();
    if (!PageFlipCtor) { this.mountLegacyFlip(); return; }

    this.mount.innerHTML = '';

    var isMobile = this.isMobile();
    // Desktop: unchanged — half of the anchored spread width, matching the
    // book's own footprint. Mobile: StPageFlip's usePortrait mode shows one
    // full page at a time, so it needs near-full-width sizing, not half a
    // spread (which was leaving large empty margins before).
    var pageWidth = isMobile ? Math.min(window.innerWidth * 0.86, 480) : (this._pageWidth || 340);
    var pageHeight = isMobile ? Math.min(window.innerHeight * 0.7, 640) : (this._pageHeight || 480);

    try {
      this.pageFlip = new PageFlipCtor(this.mount, {
        width: Math.round(pageWidth),
        height: Math.round(pageHeight),
        size: 'fixed',
        minWidth: 220,
        maxWidth: 1000,
        minHeight: 320,
        maxHeight: 1400,
        showCover: this.showCover,
        maxShadowOpacity: 0.55,
        drawShadow: true,
        flippingTime: 820,
        usePortrait: isMobile,
        mobileScrollSupport: false,
        useMouseEvents: true,
        swipeDistance: 30,
        clickEventForward: true
      });

      this.pageFlip.loadFromImages(this.pageUrls);
      this.pageFlip.on('flip', function (e) { self.updatePageCount(e.data); });
      this.updatePageCount(0);
    } catch (err) {
      // If the library throws for any reason, degrade gracefully rather
      // than leaving a broken/empty panel.
      this.usingLegacyFallback = true;
      this.mountLegacyFlip();
    }
  };

  AbeersBook.prototype.turnNext = function () {
    if (this.pageFlip) { this.pageFlip.flipNext(); return; }
    if (this.legacy) this.legacy.next();
  };
  AbeersBook.prototype.turnPrev = function () {
    if (this.pageFlip) { this.pageFlip.flipPrev(); return; }
    if (this.legacy) this.legacy.prev();
  };

  AbeersBook.prototype.updatePageCount = function (index) {
    if (!this.pageCountEl) return;
    var current = (index || 0) + 1;
    this.pageCountEl.textContent = current + ' / ' + this.totalPages;
    if (this.prevBtn) this.prevBtn.disabled = current <= 1;
    if (this.nextBtn) this.nextBtn.disabled = current >= this.totalPages;
  };

  // ---- Legacy fallback: only used if StPageFlip can't load at all ----
  // A simple, still-smooth cross-fade between spreads. No curl physics,
  // but guarantees the brochure is never just broken/blank.
  AbeersBook.prototype.mountLegacyFlip = function () {
    var self = this;
    this.mount.innerHTML = '';
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative;width:100%;height:100%;display:flex;';
    var img = document.createElement('img');
    img.style.cssText = 'width:100%;height:100%;object-fit:contain;transition:opacity .35s ease;';
    wrap.appendChild(img);
    this.mount.appendChild(wrap);

    var index = 0;
    var render = function () {
      img.style.opacity = 0;
      setTimeout(function () {
        img.src = self.pageUrls[index];
        img.style.opacity = 1;
      }, 150);
      self.updatePageCount(index);
    };
    render();

    this.legacy = {
      next: function () { if (index < self.totalPages - 1) { index++; render(); } },
      prev: function () { if (index > 0) { index--; render(); } }
    };
  };

  AbeersBook.prototype.teardownReader = function () {
    if (this.pageFlip) {
      try { this.pageFlip.destroy(); } catch (e) { /* no-op */ }
      this.pageFlip = null;
    }
    this.legacy = null;
    if (this.mount) this.mount.innerHTML = '';
    this.showLoading(false);
  };

  AbeersBook.prototype.close = function () {
    var self = this;
    if (!this.isOpen) return;
    this.isOpen = false;

    this.shell.classList.remove('is-active');
    if (!this.isMobile()) this.shell.style.transform = 'scaleX(0.5)';
    this.book.classList.remove('is-open');
    document.body.style.overflow = '';

    this._closeCleanupTimer = setTimeout(function () {
      self.teardownReader();
      self._closeCleanupTimer = null;
    }, 650);
  };

  function init() {
    document.querySelectorAll('[data-aw-book-root]').forEach(function (root) {
      new AbeersBook(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-aw-book-root]');
    if (root) new AbeersBook(root);
  });
})();